import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";

const STEPS = [
  {
    img: "/tour/overview.jpg",
    title: "Your Command Center",
    body: "One glance shows project progress, milestones and exactly what needs your attention — no chasing updates.",
  },
  {
    img: "/tour/project.jpg",
    title: "Live Project Tracking",
    body: "Every phase of the H.U.G.H. Method™ tracked in real time, with a running activity feed of everything we do.",
  },
  {
    img: "/tour/design.jpg",
    title: "Design Review & Approvals",
    body: "Review design directions and approve them in one click — or request changes without a single email.",
  },
  {
    img: "/tour/assets.jpg",
    title: "Brand Asset Uploads",
    body: "Drop your logo, photos and brand materials straight into the portal so the build never waits on attachments.",
  },
  {
    img: "/tour/billing.jpg",
    title: "Transparent Billing",
    body: "Invoices, secure Stripe payments and downloadable receipts — your full payment picture in one place.",
  },
  {
    img: "/tour/messages.jpg",
    title: "Direct Line to Your Team",
    body: "Message us right inside the portal. Every conversation stays attached to your project, never lost in an inbox.",
  },
];

export const PortalTour = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const last = step === STEPS.length - 1;

  const next = useCallback(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), []);
  const prev = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    STEPS.forEach((s) => { const i = new Image(); i.src = s.img; });
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose, next, prev]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-md lg:p-8"
          onClick={onClose}
          data-testid="portal-tour-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl overflow-hidden rounded-2xl border border-[#2A2D34] bg-[#0C0E11]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#2A2D34] px-5 py-3.5">
              <div className="flex items-center gap-3">
                <span className="text-xs font-black uppercase tracking-[0.25em] text-[#C6FF00]">Portal Tour</span>
                <span className="text-xs font-semibold text-[#6B7280]" data-testid="tour-step-count">
                  {step + 1} / {STEPS.length}
                </span>
              </div>
              <button onClick={onClose} data-testid="tour-close-btn" aria-label="Close tour"
                className="rounded-lg p-1.5 text-[#8A939B] transition-colors hover:bg-white/5 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="relative bg-[#0A0C0F]">
              <div className="flex items-center gap-1.5 border-b border-[#2A2D34] bg-[#14171C] px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
                <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
                <span className="h-2 w-2 rounded-full bg-[#28C840]" />
                <span className="ml-3 rounded-md bg-[#0A0C0F] px-3 py-0.5 text-[10px] font-semibold tracking-wide text-[#6B7280]">
                  portal.hughlaunch.com
                </span>
              </div>
              <div className="relative aspect-[16/10] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={step}
                    src={STEPS[step].img}
                    alt={STEPS[step].title}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="absolute inset-0 h-full w-full object-cover object-top"
                    data-testid="tour-step-image"
                  />
                </AnimatePresence>
              </div>
            </div>

            <div className="flex flex-col gap-4 border-t border-[#2A2D34] p-5 sm:flex-row sm:items-center lg:px-7">
              <div className="min-w-0 flex-1">
                <p className="font-black uppercase tracking-tight text-white" data-testid="tour-step-title">{STEPS[step].title}</p>
                <p className="mt-1 text-sm text-[#8A939B] font-medium">{STEPS[step].body}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <div className="mr-1 hidden items-center gap-1.5 sm:flex">
                  {STEPS.map((_, i) => (
                    <button key={i} onClick={() => setStep(i)} aria-label={`Step ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-6 bg-[#C6FF00]" : "w-1.5 bg-[#2A2D34] hover:bg-[#4B5563]"}`} />
                  ))}
                </div>
                <button onClick={prev} disabled={step === 0} data-testid="tour-prev-btn" aria-label="Previous"
                  className="rounded-full border border-[#2A2D34] p-2.5 text-white transition-colors hover:border-[#C6FF00] hover:text-[#C6FF00] disabled:opacity-30 disabled:hover:border-[#2A2D34] disabled:hover:text-white">
                  <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
                </button>
                {last ? (
                  <Link to="/brand-audit" data-testid="tour-cta-btn"
                    className="btn-shine flex items-center gap-2 whitespace-nowrap rounded-full bg-[#C6FF00] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0A0C0F] transition-colors hover:bg-white">
                    Get Your Free Brand Audit <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </Link>
                ) : (
                  <button onClick={next} data-testid="tour-next-btn"
                    className="flex items-center gap-2 rounded-full bg-[#C6FF00] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0A0C0F] transition-colors hover:bg-white">
                    Next <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
